import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatCampaignDate } from "../../utils/campaignDisplayUtils";
import useScrollSelectedIntoView from "../../hooks/useScrollSelectedIntoView";
import CampaignCard from "./CampaignCard";
import CampaignListItem from "./CampaignListItem";

const getLatestCampaignCreatedDate = (campaigns) => {
    const latestTimestamp = campaigns.reduce((latest, campaign) => {
        if (!campaign?.created_at) return latest;

        const timestamp = new Date(campaign.created_at).getTime();

        if (Number.isNaN(timestamp)) return latest;

        return Math.max(latest, timestamp);
    }, 0);

    if (!latestTimestamp) return "";

    return formatCampaignDate(new Date(latestTimestamp).toISOString());
};

const getCampaignSequenceId = (campaign) =>
    String(campaign?.sequence_id || campaign?.id || "").trim();

const CampaignGroup = ({
    searchQuery,
    campaigns = [],
    selectedCampaignDetails = null,
    onEditCampaignContext,
    onUpdateCampaignStatus,
    onViewCampaign,
    onSelectCampaign,
    selectedSequenceId: targetSequenceId,
    scrollTargetSequenceId = "",
    onScrollTargetResolved,
    updatingStatusSequenceId,
    autoExpand = false,
    initiallyExpanded = false,
    variant = "card",
}) => {
    const normalizedTargetSequenceId = String(targetSequenceId || "").trim();
    const normalizedScrollTargetSequenceId = String(
        scrollTargetSequenceId || "",
    ).trim();
    const hasTargetSequence = campaigns.some(
        (campaign) =>
            getCampaignSequenceId(campaign) === normalizedTargetSequenceId,
    );
    const [isGroupExpanded, setIsGroupExpanded] = useState(
        hasTargetSequence || initiallyExpanded,
    );
    const [selectedSequenceId, setSelectedSequenceId] = useState(
        hasTargetSequence
            ? normalizedTargetSequenceId
            : getCampaignSequenceId(campaigns[0]) || null,
    );
    const lastRequestedDetailsSequenceId = useRef("");
    const isGroupVisible = isGroupExpanded || autoExpand;

    useEffect(() => {
        if (!normalizedTargetSequenceId) return;

        const targetCampaignExists = campaigns.some(
            (campaign) =>
                getCampaignSequenceId(campaign) ===
                normalizedTargetSequenceId,
        );

        if (!targetCampaignExists) return;

        setSelectedSequenceId(normalizedTargetSequenceId);
        setIsGroupExpanded(true);
    }, [campaigns, normalizedTargetSequenceId, targetSequenceId]);

    useEffect(() => {
        if (!Array.isArray(campaigns) || campaigns.length === 0) {
            setSelectedSequenceId(null);
            return;
        }

        const selectedCampaignExists = campaigns.some(
            (campaign) => getCampaignSequenceId(campaign) === selectedSequenceId,
        );

        if (!selectedCampaignExists) {
            setSelectedSequenceId(getCampaignSequenceId(campaigns[0]) || null);
        }
    }, [campaigns, selectedSequenceId]);

    const selectedCampaign =
        campaigns.find(
            (campaign) => getCampaignSequenceId(campaign) === selectedSequenceId,
        ) || campaigns[0];
    const selectedCampaignDetailsSequenceId =
        getCampaignSequenceId(selectedCampaignDetails);
    const selectedCampaignDetailsForCard =
        selectedCampaignDetailsSequenceId &&
        selectedCampaignDetailsSequenceId ===
            getCampaignSequenceId(selectedCampaign)
            ? selectedCampaignDetails
            : null;

    useEffect(() => {
        const sequenceId = getCampaignSequenceId(selectedCampaign);

        if (!isGroupVisible || !sequenceId) return;
        if (lastRequestedDetailsSequenceId.current === sequenceId) return;

        lastRequestedDetailsSequenceId.current = sequenceId;
        onSelectCampaign?.(selectedCampaign);
    }, [isGroupVisible, onSelectCampaign, selectedCampaign]);

    const handleSelectCampaign = (sequenceId) => {
        const campaign = campaigns.find(
            (item) => getCampaignSequenceId(item) === sequenceId,
        );

        setSelectedSequenceId(sequenceId);
        lastRequestedDetailsSequenceId.current = sequenceId;
        onSelectCampaign?.(campaign || { sequence_id: sequenceId });
    };
    const shouldScrollSelectedCampaign =
        isGroupVisible &&
        Boolean(normalizedScrollTargetSequenceId) &&
        selectedSequenceId === normalizedScrollTargetSequenceId;
    const handleSelectedCampaignScrolled = useCallback(() => {
        onScrollTargetResolved?.(normalizedScrollTargetSequenceId);
    }, [normalizedScrollTargetSequenceId, onScrollTargetResolved]);
    const selectedCampaignItemRef = useScrollSelectedIntoView({
        selectedId: normalizedScrollTargetSequenceId,
        enabled: shouldScrollSelectedCampaign,
        onScrolled: handleSelectedCampaignScrolled,
    });

    const latestCampaignCreatedDate = useMemo(
        () => getLatestCampaignCreatedDate(campaigns),
        [campaigns],
    );

    if (!Array.isArray(campaigns) || campaigns.length === 0) return null;

    const isRowVariant = variant === "row";

    return (
        <section
            className={
                isRowVariant
                    ? `border-b border-slate-200 transition-colors ${
                          isGroupVisible
                              ? "border-l-4 border-l-transparent odd:bg-slate-50 even:bg-white"
                              : "border-l-4 border-l-transparent odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                      }`
                    : "rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition sm:rounded-2xl sm:p-4 sm:hover:border-indigo-100"
            }
        >
            <button
                type="button"
                aria-expanded={isGroupVisible}
                onClick={() =>
                    !autoExpand &&
                    setIsGroupExpanded((currentValue) => !currentValue)
                }
                className={`flex w-full flex-col gap-2 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${
                    isRowVariant ? "px-4 py-3" : ""
                }`}
            >
                <div className="flex w-full min-w-0 items-start justify-between gap-2 sm:w-auto sm:items-center sm:justify-start">
                    <p
                        className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900"
                    >
                        <span className="mr-1 text-base font-semibold text-slate-500">
                            Your search :
                        </span>
                        <span>
                            {searchQuery || "Unknown search"}
                        </span>
                    </p>

                    <span className="inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                        {campaigns.length}{" "}
                        {campaigns.length === 1 ? "Campaign" : "Campaigns"}
                    </span>
                </div>

                <div className="flex w-full shrink-0 items-center justify-between gap-3 text-sm text-slate-500 sm:w-auto sm:justify-start">
                    {latestCampaignCreatedDate && (
                        <span>
                            Latest campaign created:{" "}
                            <span className="font-medium text-slate-700">
                                {latestCampaignCreatedDate}
                            </span>
                        </span>
                    )}

                    <span className="hidden h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm sm:flex">
                        {isGroupVisible ? (
                            <ChevronDown size={18} aria-hidden="true" />
                        ) : (
                            <ChevronRight size={18} aria-hidden="true" />
                        )}
                    </span>
                </div>
            </button>

            {isGroupVisible && (
                <div
                    className={`grid min-w-0 items-stretch gap-3 xl:grid-cols-[460px_minmax(0,1fr)] 2xl:grid-cols-[460px_minmax(0,1fr)] ${
                        isRowVariant ? "px-4 pb-4" : "mt-3"
                    }`}
                >
                    <aside className="flex h-full max-h-60 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white pt-4 shadow-sm xl:max-h-none">
                        <div className="mb-2 shrink-0 px-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                                <span className="sm:hidden">Campaign List</span>
                                <span className="hidden sm:inline">
                                    Associated Campaigns of search : {searchQuery}
                                </span>
                            </p>
                        </div>
                        <div className="campaign-scrollbar min-h-0 flex-1 overflow-y-auto">
                            {campaigns.map((campaign, index) => {
                                const campaignSequenceId =
                                    getCampaignSequenceId(campaign);
                                const isSelected =
                                    campaignSequenceId ===
                                    getCampaignSequenceId(selectedCampaign);

                                return (
                                    <CampaignListItem
                                        key={
                                            campaignSequenceId ||
                                            `${campaign.name || "campaign"}-${index}`
                                        }
                                        campaign={campaign}
                                        isSelected={isSelected}
                                        selectedItemRef={
                                            shouldScrollSelectedCampaign &&
                                            campaignSequenceId ===
                                                normalizedScrollTargetSequenceId
                                                ? selectedCampaignItemRef
                                                : null
                                        }
                                        onSelect={handleSelectCampaign}
                                    />
                                );
                            })}
                        </div>
                    </aside>

                    <section className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-3 sm:p-4 xl:border-l-2 xl:border-l-indigo-100">
                        <div className="mb-3 shrink-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                                Campaign Details
                            </p>
                        </div>
                        {selectedCampaign && (
                            <CampaignCard
                                campaign={selectedCampaign}
                                campaignDetails={selectedCampaignDetailsForCard}
                                isStatusUpdating={
                                    selectedCampaign.sequence_id ===
                                    updatingStatusSequenceId
                                }
                                onEditCampaignContext={onEditCampaignContext}
                                onUpdateCampaignStatus={
                                    onUpdateCampaignStatus
                                }
                                onViewCampaign={onViewCampaign}
                            />
                        )}
                    </section>
                </div>
            )}
        </section>
    );
};

export default CampaignGroup;
